import { useContext, useState } from "react";
import { Button, Card, Empty, Pagination, Tag, Tooltip, message } from "antd";
import Text from "antd/es/typography/Text";
import {
    CheckOutlined,
    ClockCircleOutlined,
    CopyOutlined,
    DownloadOutlined,
    TrophyOutlined,
    PlayCircleOutlined,
} from "@ant-design/icons";

import { ITableData } from "../../useHoldGiveaway";
import { formatDateStringToDateAndTime } from "../../../../../utils/Conversores";
import { ThemeContext } from "../../../../../contexts/ThemeContext";

interface IProps {
    historyList: ITableData[];
    onDownloadFile: (fileName: string) => void;
    onViewWinners: (attemptNumber: number) => void;
    onShowLiveDraw?: (attemptNumber: number) => void;
    isDownloadingFile?: boolean;
    loading?: boolean;
}

export default function GiveawayHistoryCards({
    historyList,
    onDownloadFile,
    onViewWinners,
    onShowLiveDraw,
    isDownloadingFile,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [currentPage, setCurrentPage] = useState<number>(1);
    const pageSize = 5;

    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const handleCopy = async (text: string, key: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(key);
            message.success("Hash copiado com sucesso!");
            setTimeout(() => setCopiedKey(null), 2500);
        } catch {
            message.error("Erro ao copiar hash.");
        }
    };

    if (!historyList || historyList.length === 0) {
        return (
            <Card
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    borderRadius: 12,
                    border: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                    boxShadow: isDark
                        ? "0 4px 20px rgba(0, 0, 0, 0.35)"
                        : "0 2px 10px rgba(0, 0, 0, 0.03)",
                }}
            >
                <Empty
                    description={
                        <Text type="secondary" style={{ fontSize: 14 }}>
                            Nenhum histórico registrado até o momento.
                        </Text>
                    }
                    style={{ padding: "30px 0" }}
                />
            </Card>
        );
    }

    // Sort descending by created_at
    const sortedList = [...historyList].sort((a, b) => {
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return timeB - timeA;
    });

    const paginatedList = sortedList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {paginatedList.map((history, index) => {
                const isRaffleAttempt =
                    history.attempt_number !== null && history.attempt_number !== undefined;

                const isSorteioRealizado =
                    Boolean(history.observation?.includes("Sorteio realizado!")) ||
                    Boolean((history as any).status?.includes("Sorteio realizado!")) ||
                    Boolean(history.observation?.toLowerCase().includes("sorteio realizado"));

                const showParticipantsHash =
                    Boolean(history.hash_with_participants) && !isSorteioRealizado;

                const showWinnersHash = Boolean(history.hash_with_winners);

                const hasHashes = showParticipantsHash || showWinnersHash;

                const canViewWinners =
                    Boolean(history.attempt_number) &&
                    Boolean(history.file_with_winners) &&
                    (isSorteioRealizado || showWinnersHash);

                const anyHistory = history as any;

                return (
                    <Card
                        key={history.id || index}
                        style={{
                            background: isDark ? "#141414" : "#ffffff",
                            borderRadius: 12,
                            border: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                            boxShadow: isDark
                                ? "0 2px 10px rgba(0, 0, 0, 0.25)"
                                : "0 1px 4px rgba(0, 0, 0, 0.03)",
                        }}
                        bodyStyle={{ padding: "16px 20px" }}
                    >
                        {/* Header: Badge + Full Observation Text + User + Date + Winners Button */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 10,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                    flex: 1,
                                    minWidth: 240,
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                    {isRaffleAttempt && history.attempt_number && (
                                        <Tag
                                            color="blue"
                                            style={{
                                                fontWeight: 700,
                                                borderRadius: 6,
                                                margin: 0,
                                                fontSize: 12,
                                                padding: "1px 8px",
                                            }}
                                        >
                                            Apuração #{history.attempt_number}
                                        </Tag>
                                    )}

                                    <span
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 14,
                                            color: isDark ? "#f1f5f9" : "#0f172a",
                                        }}
                                    >
                                        {history.observation ||
                                            anyHistory.description ||
                                            anyHistory.details ||
                                            anyHistory.message ||
                                            anyHistory.text ||
                                            anyHistory.status ||
                                            "Registro de alteração"}
                                    </span>

                                    {anyHistory.status &&
                                        !String(history.observation || anyHistory.description || '').includes(anyHistory.status) && (
                                            <Tag
                                                color="cyan"
                                                style={{
                                                    borderRadius: 6,
                                                    margin: 0,
                                                    fontWeight: 500,
                                                    fontSize: 12,
                                                }}
                                            >
                                                {anyHistory.status}
                                            </Tag>
                                        )}
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        color: isDark ? "#a6a6a6" : "#64748b",
                                        fontSize: 12,
                                    }}
                                >
                                    {history.created_at && (
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 4,
                                            }}
                                        >
                                            <ClockCircleOutlined style={{ fontSize: 11 }} />
                                            {formatDateStringToDateAndTime(history.created_at)}
                                        </span>
                                    )}

                                    {(anyHistory.panel_user?.name || anyHistory.user?.name) && (
                                        <span>
                                            por{" "}
                                            <strong>
                                                {anyHistory.panel_user?.name || anyHistory.user?.name}
                                            </strong>
                                        </span>
                                    )}
                                </div>
                            </div>

                            {canViewWinners && (
                                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                    {onShowLiveDraw && (
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<PlayCircleOutlined />}
                                            onClick={() =>
                                                history.attempt_number && onShowLiveDraw(history.attempt_number)
                                            }
                                            style={{
                                                borderRadius: 6,
                                                fontWeight: 600,
                                                fontSize: 12,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 4,
                                                background: "linear-gradient(135deg, #eab308 0%, #ca8a04 100%)",
                                                borderColor: "#ca8a04",
                                                color: "#0f172a",
                                                boxShadow: "0 2px 8px rgba(234, 179, 8, 0.3)",
                                            }}
                                        >
                                            Apuração
                                        </Button>
                                    )}

                                    <Button
                                        type="default"
                                        size="small"
                                        icon={<TrophyOutlined style={{ color: "#faad14" }} />}
                                        onClick={() =>
                                            history.attempt_number && onViewWinners(history.attempt_number)
                                        }
                                        style={{
                                            borderRadius: 6,
                                            fontWeight: 500,
                                            fontSize: 12,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                        }}
                                    >
                                        Ver Ganhadores
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* If hashes exist (e.g. participant hash or winner hash) */}
                        {hasHashes && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 10,
                                    marginTop: 12,
                                    paddingTop: 12,
                                    borderTop: `1px solid ${isDark ? "#262626" : "#f1f5f9"}`,
                                }}
                            >
                                {showParticipantsHash && (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            flexWrap: "wrap",
                                            gap: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                flex: 1,
                                                minWidth: 260,
                                            }}
                                        >
                                            <Text
                                                strong
                                                style={{
                                                    fontSize: 12,
                                                    minWidth: 95,
                                                    color: isDark ? "#93c5fd" : "#1d4ed8",
                                                }}
                                            >
                                                Participantes:
                                            </Text>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    flex: 1,
                                                    background: isDark ? "#1a1a1a" : "#f8fafc",
                                                    padding: "4px 10px",
                                                    borderRadius: 6,
                                                    border: `1px solid ${isDark ? "#2d2d2d" : "#e2e8f0"}`,
                                                }}
                                            >
                                                <Text
                                                    code
                                                    style={{
                                                        fontSize: 11,
                                                        fontFamily: "monospace",
                                                        wordBreak: "break-all",
                                                        margin: 0,
                                                        padding: 0,
                                                        border: "none",
                                                        background: "transparent",
                                                        flex: 1,
                                                        color: isDark ? "#e2e8f0" : "#334155",
                                                    }}
                                                >
                                                    {history.hash_with_participants}
                                                </Text>
                                                <Tooltip title="Copiar Hash">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={
                                                            copiedKey === `part_${history.id || index}` ? (
                                                                <CheckOutlined style={{ color: "#52c41a" }} />
                                                            ) : (
                                                                <CopyOutlined />
                                                            )
                                                        }
                                                        onClick={() =>
                                                            handleCopy(
                                                                history.hash_with_participants!,
                                                                `part_${history.id || index}`
                                                            )
                                                        }
                                                        style={{ height: 22, width: 22, padding: 0 }}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </div>

                                        {history.file_with_participants && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                loading={isDownloadingFile}
                                                onClick={() =>
                                                    onDownloadFile(history.file_with_participants!)
                                                }
                                                style={{
                                                    fontSize: 12,
                                                    padding: "0 4px",
                                                    height: "auto",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                }}
                                            >
                                                Baixar Bilhetes
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {showWinnersHash && (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            flexWrap: "wrap",
                                            gap: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                flex: 1,
                                                minWidth: 260,
                                            }}
                                        >
                                            <Text
                                                strong
                                                style={{
                                                    fontSize: 12,
                                                    minWidth: 95,
                                                    color: isDark ? "#86efac" : "#15803d",
                                                }}
                                            >
                                                Ganhadores:
                                            </Text>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    flex: 1,
                                                    background: isDark
                                                        ? "rgba(82, 196, 26, 0.05)"
                                                        : "#f6ffed",
                                                    padding: "4px 10px",
                                                    borderRadius: 6,
                                                    border: `1px solid ${isDark ? "rgba(82, 196, 26, 0.2)" : "#d9f7be"
                                                        }`,
                                                }}
                                            >
                                                <Text
                                                    code
                                                    style={{
                                                        fontSize: 11,
                                                        fontFamily: "monospace",
                                                        wordBreak: "break-all",
                                                        margin: 0,
                                                        padding: 0,
                                                        border: "none",
                                                        background: "transparent",
                                                        flex: 1,
                                                        color: isDark ? "#86efac" : "#15803d",
                                                    }}
                                                >
                                                    {history.hash_with_winners}
                                                </Text>
                                                <Tooltip title="Copiar Hash">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={
                                                            copiedKey === `win_${history.id || index}` ? (
                                                                <CheckOutlined style={{ color: "#52c41a" }} />
                                                            ) : (
                                                                <CopyOutlined />
                                                            )
                                                        }
                                                        onClick={() =>
                                                            handleCopy(
                                                                history.hash_with_winners!,
                                                                `win_${history.id || index}`
                                                            )
                                                        }
                                                        style={{ height: 22, width: 22, padding: 0 }}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </div>

                                        {history.file_with_winners && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                loading={isDownloadingFile}
                                                onClick={() =>
                                                    onDownloadFile(history.file_with_winners!)
                                                }
                                                style={{
                                                    fontSize: 12,
                                                    padding: "0 4px",
                                                    height: "auto",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    color: "#52c41a",
                                                }}
                                            >
                                                Baixar Resultado
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                );
            })}

            {/* Pagination if more than pageSize */}
            {sortedList.length > pageSize && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={sortedList.length}
                        onChange={(page) => setCurrentPage(page)}
                        showTotal={(total, range) =>
                            `Mostrando ${range[0]}-${range[1]} de ${total} registros`
                        }
                        size="small"
                    />
                </div>
            )}
        </div>
    );
}
